import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Card,
  CardContent,
  CardActions,
  AppBar,
  Toolbar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { useSections } from "./SectionContext";

const sectionRoutes = {
  "Photo, Personal Details": "/PersonalDetail",
  "Websites, Portfolios, Profiles": "/WebsiteSection",
  Certifications: "/Certifications",
  Languages: "/Language",
  Accomplishments: "/AccomplishmentsSection",
  "Additional Information": "/AdditionalInfoForm",
  Software: "/SoftWare",
  References: "/Refferences",
  Volunteering: "/Volunteer",
  Interests: "/Interest",
};

const References = () => {
  const [content, setContent] = useState("");
  const [savedRefs, setSavedRefs] = useState([]);
  const [editIndex, setEditIndex] = useState(null);

  const { sections } = useSections();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);

  useEffect(() => {
    if (location.state) {
      const { selectedTemplate: template, selectedTheme: theme } = location.state;
      if (template) setSelectedTemplate(template);
      if (theme) setSelectedTheme(theme);
    }
  }, [location.state]);

  // Load saved references from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("referencesList");
    if (saved) {
      setSavedRefs(JSON.parse(saved));
    }
  }, []);

  const handleEdit = (index) => {
    setContent(savedRefs[index]);
    setEditIndex(index);
  };

  const handleDelete = (index) => {
    const updatedList = savedRefs.filter((_, i) => i !== index);
    setSavedRefs(updatedList);
    localStorage.setItem("referencesList", JSON.stringify(updatedList));
  };

  const handleSaveAndNext = () => {
    // Save reference if content exists
    if (content.trim()) {
      let updatedList;
      if (editIndex !== null) {
        updatedList = [...savedRefs];
        updatedList[editIndex] = content;
        setEditIndex(null);
      } else {
        updatedList = [...savedRefs, content];
      }

      setSavedRefs(updatedList);
      localStorage.setItem("referencesList", JSON.stringify(updatedList));
      setContent("");
    }

    // Navigate to next section
    const currentSection = "References";
    const currentIndex = sections.indexOf(currentSection);

    if (currentIndex < sections.length - 1) {
      const nextSection = sections[currentIndex + 1];
      navigate(sectionRoutes[nextSection], {
        state: { selectedTemplate, selectedTheme },
      });
    } else {
      navigate("/mainpage", {
        state: { selectedTemplate, selectedTheme },
      });
    }
  };

  return (
    <Box sx={{ backgroundColor: "#EFF7FF", minHeight: "100vh" }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: "#002B5B" }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: "bold", letterSpacing: 0.5 }}>
            References
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          p: 4,
          maxWidth: "800px",
          mx: "auto",
        }}
      >
        {/* Section Title */}
        <Typography
          variant="h6"
          sx={{
            color: "#002B5B",
            fontWeight: "bold",
            mb: 2,
            borderBottom: "2px solid #0288D1",
            pb: 1,
          }}
        >
          Add References
        </Typography>

        {/* Editable Text Field */}
        <TextField
          id="references-box"
          fullWidth
          multiline
          rows={6}
          placeholder="Enter your references here..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          sx={{
            backgroundColor: "#ffffff",
            borderRadius: 2,
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "#B3D9F7" },
              "&:hover fieldset": { borderColor: "#0288D1" },
              "&.Mui-focused fieldset": { borderColor: "#002B5B" },
            },
          }}
        />

        {/* Save & Next Button */}
        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveAndNext}
            sx={{
              px: 4,
              fontWeight: "bold",
              backgroundColor: "#002B5B",
              "&:hover": { backgroundColor: "#0288D1" },
              borderRadius: 2,
              boxShadow: "0 3px 10px rgba(0, 43, 91, 0.3)",
            }}
          >
            Save & Next
          </Button>
        </Box>

        {/* Saved References */}
        <Box mt={4}>
          {savedRefs.length > 0 && (
            <Typography
              variant="h6"
              mb={2}
              sx={{
                color: "#002B5B",
                fontWeight: "bold",
                borderBottom: "2px solid #0288D1",
                pb: 1,
              }}
            >
              Saved References
            </Typography>
          )}
          {savedRefs.map((item, index) => (
            <Card
              key={index}
              sx={{
                mb: 2,
                boxShadow: "0 2px 10px rgba(2, 136, 209, 0.15)",
                border: "1px solid #B3D9F7",
                borderLeft: "4px solid #002B5B",
                borderRadius: 2,
                backgroundColor: "#ffffff",
                "&:hover": { boxShadow: "0 4px 16px rgba(0, 43, 91, 0.2)" },
              }}
            >
              <CardContent>
                <Typography variant="body2" whiteSpace="pre-line" sx={{ color: "#0D4C8B" }}>
                  {item}
                </Typography>
              </CardContent>
              <CardActions>
                <IconButton
                  onClick={() => handleEdit(index)}
                  sx={{ color: "#0288D1", "&:hover": { color: "#002B5B" } }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDelete(index)} color="error">
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default References;